// UserAppPairAllowlistManager — admin page at /settings/user-app-pair-allowlist.
// Backs the skip_if_in_user_app_pair_allowlist suppression tier (v1.83.0): per
// (rule, tenant) (user, application) pairs whose matches are suppressed. The
// pipeline matches on the application GUID (the authoritative key); the display
// name is informational. Wildcards '*' allowed on either side (not both). Read-open,
// admin-write. Soft-delete (enabled=false). Mirrors AccountAllowlistManager (rule
// tabs / resize / paginate / sentinel health + reconcile), extended with the app
// dimension + a Known-Apps-backed application picker (with manual-GUID fallback).
// The primary seeding path is the quick-suppress button on the Detection Alerts page.

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  CubeTransparentIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  LockClosedIcon,
  ArrowsRightLeftIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { format, parseISO } from 'date-fns';
import {
  fetchUserAppPairAllowlist,
  fetchUserAppPairAllowlistSentinelHealth,
  addUserAppPairAllowlistEntry,
  updateUserAppPairAllowlistEntry,
  removeUserAppPairAllowlistEntry,
  reconcileUserAppPairAllowlistSentinel,
  fetchTenants,
  fetchKnownApps,
  fetchDetectionRules,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import Badge from './ui/Badge';
import Pagination from './Pagination';

// "BA: Sign-in from User on No DRMM-Managed Devices" — the launch consumer; kept as
// the graceful-degradation fallback when the (admin-only) detection-rules list is
// unavailable. The enabled rule set is otherwise discovered from each rule's
// skip_if_in_user_app_pair_allowlist toggle.
const LAUNCH_RULE_ID = 'rule_b36c2eb1-ed1a-4ed3-9d0f-91edd4c4c358';
const LAUNCH_FALLBACK = { ruleId: LAUNCH_RULE_ID, ruleName: 'BA: Sign-in from User on No DRMM-Managed Devices' };
const UPN_REGEX = /^[^@\s|]+@[^@\s|]+\.[^@\s|]+$/;
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Domain part of a *@domain.tld wildcard: at least one dot, a non-empty TLD, no '*', '@', whitespace or '|'.
const DOMAIN_REGEX = /^[^@\s|*]+\.[^@\s|*]+$/;

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return format(parseISO(dateStr), 'MMM d, yyyy'); } catch { return dateStr; }
}

// Domain-wildcard entries are stored as '*@domain.tld' (v1.86.0). Returns the bare
// domain (e.g. 'westerncomputer.com') for a wildcard value, else null.
function domainWildcardOf(upn) {
  if (typeof upn === 'string' && upn.startsWith('*@') && upn !== '*@*') return upn.slice(2);
  return null;
}

function validateAddForm({ tenantId, userPrincipalName, applicationId, note }) {
  if (!tenantId) return 'Tenant is required.';
  if (tenantId !== '*' && !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(tenantId)) {
    return "Tenant must be a GUID or '*'.";
  }
  if (!userPrincipalName) return 'User is required (full UPN, a domain, or *).';
  if (userPrincipalName !== '*') {
    if (userPrincipalName.includes('|')) return "User cannot contain '|'.";
    if (userPrincipalName.length > 256) return 'User exceeds 256 chars.';
    if (userPrincipalName.startsWith('*@')) {
      // Domain-wildcard entry: '*@domain.tld' (matches any B2B guest from that domain).
      if (userPrincipalName === '*@*') return "The '*@*' (any domain) wildcard is not allowed.";
      if (!DOMAIN_REGEX.test(userPrincipalName.slice(2))) {
        return 'Enter a valid domain like westerncomputer.com (must include a dot and a TLD).';
      }
    } else if (!UPN_REGEX.test(userPrincipalName)) {
      return 'User must be a full UPN (foo@bar.tld) — a bare local-part is rejected by the pipeline.';
    }
  }
  if (!applicationId) return 'Application is required (app GUID or *).';
  if (applicationId !== '*' && !GUID_REGEX.test(applicationId)) {
    return "Application must be a GUID or '*'.";
  }
  if (userPrincipalName === '*' && applicationId === '*') {
    return "The (any user, any app) pair is not allowed — it would disable the rule.";
  }
  if (note && note.length > 2000) return 'Note exceeds 2000 chars.';
  return null;
}

export default function UserAppPairAllowlistManager() {
  const { user } = useAuth();
  const isAdmin = !!user?.isAdmin;

  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [allowlistRules, setAllowlistRules] = useState([]);
  const [selectedRuleId, setSelectedRuleId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterQuery, setFilterQuery] = useState('');
  const [tenantFilter, setTenantFilter] = useState('all');
  const [showDisabled, setShowDisabled] = useState(true);

  const [health, setHealth] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [confirmReconcile, setConfirmReconcile] = useState(false);

  // Known apps (for the application picker), keyed off the selected tenant.
  const [knownApps, setKnownApps] = useState([]);

  // Add form
  const [formTenantId, setFormTenantId] = useState('');
  // userMode: 'upn' (full UPN) | 'any' (*) | 'domain' (*@domain.tld — B2B guests only)
  const [formUserMode, setFormUserMode] = useState('upn');
  const [formUpn, setFormUpn] = useState('');
  const [formDomain, setFormDomain] = useState('');
  // appMode: 'pick' | 'manual' | 'any'
  const [formAppMode, setFormAppMode] = useState('pick');
  const [formAppId, setFormAppId] = useState('');
  const [formAppName, setFormAppName] = useState('');
  const [formNote, setFormNote] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);

  // Inline edit (note + display name)
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [editAppName, setEditAppName] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState(null);

  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const [sortKey, setSortKey] = useState('addedUtc');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Resizable columns: Tenant, User, Application, Enabled, Note, Added By, Date, Actions
  const [colWidths, setColWidths] = useState([180, 240, 280, 100, 220, 150, 120, 110]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);

  const startResize = useCallback((e, colIdx) => {
    e.preventDefault();
    const th = thRefs.current[colIdx];
    const table = tableRef.current;
    if (!th) return;
    const startX = e.clientX;
    const startWidth = th.offsetWidth;
    const startTableWidth = table ? table.offsetWidth : 0;
    let lastWidth = startWidth;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (moveEvt) => {
      lastWidth = Math.max(50, startWidth + moveEvt.clientX - startX);
      th.style.width = `${lastWidth}px`;
      if (table) table.style.width = `${startTableWidth + (lastWidth - startWidth)}px`;
    };
    const onMouseUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      setColWidths(prev => { const n = [...prev]; n[colIdx] = lastWidth; return n; });
      if (table) table.style.width = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, []);

  const loadMeta = async () => {
    try {
      const tenantData = await fetchTenants();
      setTenants(Array.isArray(tenantData) ? tenantData : []);
    } catch { /* best-effort */ }
    let rules = [];
    try {
      const all = await fetchDetectionRules();
      rules = (Array.isArray(all) ? all : [])
        .filter((r) => r.skipIfInUserAppPairAllowlist === true)
        .map((r) => ({ ruleId: r.ruleId, ruleName: r.ruleName || r.ruleId }));
    } catch { /* 403 / transient — fall back below */ }
    if (rules.length === 0) rules = [LAUNCH_FALLBACK];
    rules.sort((a, b) => (a.ruleName || '').localeCompare(b.ruleName || ''));
    setAllowlistRules(rules);
    setSelectedRuleId((prev) =>
      prev || rules.find((r) => r.ruleId === LAUNCH_RULE_ID)?.ruleId || rules[0].ruleId
    );
  };

  const loadEntries = async (ruleId) => {
    if (!ruleId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchUserAppPairAllowlist(ruleId, 'all');
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Failed to load user/app pair allowlist');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  const loadHealth = async () => {
    try {
      const h = await fetchUserAppPairAllowlistSentinelHealth();
      setHealth(h);
    } catch { /* best-effort */ }
  };

  useEffect(() => {
    loadMeta();
    loadHealth();
    const intervalId = setInterval(loadHealth, 30_000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    loadEntries(selectedRuleId);
    setCurrentPage(1);
  }, [selectedRuleId]);

  // Load known apps for the application picker, scoped to the form tenant (+ GLOBAL).
  useEffect(() => {
    let cancelled = false;
    const tid = formTenantId && formTenantId !== '*' ? formTenantId : undefined;
    fetchKnownApps(tid)
      .then((apps) => { if (!cancelled) setKnownApps(Array.isArray(apps) ? apps : []); })
      .catch(() => { if (!cancelled) setKnownApps([]); });
    return () => { cancelled = true; };
  }, [formTenantId]);

  const tenantNameById = useMemo(() => {
    const map = {};
    tenants.forEach((t) => { if (t.TenantID) map[t.TenantID] = t.FriendlyName || t.TenantID; });
    return map;
  }, [tenants]);

  const sortedTenants = useMemo(
    () => [...tenants].sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || '')),
    [tenants]
  );

  const sortedApps = useMemo(
    () => [...knownApps].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '')),
    [knownApps]
  );

  const filtered = useMemo(() => {
    let r = rows;
    if (!showDisabled) r = r.filter((x) => x.enabled !== false);
    if (tenantFilter !== 'all') r = r.filter((x) => x.tenantId === tenantFilter || x.tenantId === '*');
    if (filterQuery.trim()) {
      const q = filterQuery.toLowerCase();
      r = r.filter((x) =>
        (x.userPrincipalName && x.userPrincipalName.toLowerCase().includes(q)) ||
        (x.applicationId && x.applicationId.toLowerCase().includes(q)) ||
        (x.applicationDisplayName && x.applicationDisplayName.toLowerCase().includes(q)) ||
        (x.note && x.note.toLowerCase().includes(q)) ||
        (x.addedBy && x.addedBy.toLowerCase().includes(q))
      );
    }
    return r;
  }, [rows, showDisabled, tenantFilter, filterQuery]);

  const sorted = useMemo(() => {
    const result = [...filtered];
    result.sort((a, b) => {
      const av = a[sortKey] ?? '';
      const bv = b[sortKey] ?? '';
      if (av === bv) return 0;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      return sortDir === 'asc' ? 1 : -1;
    });
    return result;
  }, [filtered, sortKey, sortDir]);

  useEffect(() => { setCurrentPage(1); }, [filterQuery, tenantFilter, showDisabled, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIdx = (safeCurrentPage - 1) * pageSize;
  const paged = sorted.slice(startIdx, startIdx + pageSize);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };

  const SortIndicator = ({ k }) => {
    if (sortKey !== k) return <span className="text-gray-700 ml-1">{'↕'}</span>;
    return sortDir === 'asc' ? <ChevronUpIcon className="inline h-3 w-3 ml-1" /> : <ChevronDownIcon className="inline h-3 w-3 ml-1" />;
  };

  const resetAddForm = () => {
    setFormTenantId('');
    setFormUserMode('upn');
    setFormUpn('');
    setFormDomain('');
    setFormAppMode('pick');
    setFormAppId('');
    setFormAppName('');
    setFormNote('');
  };

  const handleAppPick = (appId) => {
    setFormAppId(appId);
    const match = knownApps.find((a) => (a.appId || '').toLowerCase() === appId.toLowerCase());
    setFormAppName(match ? (match.displayName || '') : '');
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const effectiveUser = formUserMode === 'any'
      ? '*'
      : formUserMode === 'domain'
        ? `*@${formDomain.trim().toLowerCase().replace(/^\*@/, '')}`
        : formUpn.trim().toLowerCase();
    const effectiveAppId = formAppMode === 'any' ? '*' : formAppId.trim().toLowerCase();
    const effectiveAppName = formAppMode === 'any' ? '' : formAppName.trim();

    const validationErr = validateAddForm({
      tenantId: formTenantId,
      userPrincipalName: effectiveUser,
      applicationId: effectiveAppId,
      note: formNote,
    });
    if (validationErr) { setAddError(validationErr); return; }

    setAdding(true);
    setAddError(null);
    try {
      const created = await addUserAppPairAllowlistEntry({
        ruleId:                 selectedRuleId,
        tenantId:               formTenantId,
        userPrincipalName:      effectiveUser,
        applicationId:          effectiveAppId,
        applicationDisplayName: effectiveAppName,
        note:                   formNote.trim(),
        enabled:                true,
      });
      setRows((prev) => {
        const ix = prev.findIndex((r) => r.partitionKey === created.partitionKey && r.rowKey === created.rowKey);
        if (ix >= 0) { const next = [...prev]; next[ix] = created; return next; }
        return [created, ...prev];
      });
      resetAddForm();
      loadHealth();
    } catch (err) {
      setAddError(err.message || 'Failed to add entry');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (row) => {
    setEditError(null);
    setConfirmRemove(null);
    setEditingId(`${row.partitionKey}::${row.rowKey}`);
    setEditNote(row.note || '');
    setEditAppName(row.applicationDisplayName || '');
  };

  const cancelEdit = () => { setEditingId(null); setEditNote(''); setEditAppName(''); setEditError(null); };

  const handleSaveEdit = async (row) => {
    if (editNote && editNote.length > 2000) { setEditError('Note exceeds 2000 chars.'); return; }
    if (editAppName && editAppName.length > 256) { setEditError('Display name exceeds 256 chars.'); return; }
    setSavingEdit(true);
    setEditError(null);
    try {
      const updated = await updateUserAppPairAllowlistEntry(row.partitionKey, row.rowKey, {
        note: editNote.trim(),
        applicationDisplayName: editAppName.trim(),
      });
      setRows((prev) => prev.map((x) => (x.partitionKey === row.partitionKey && x.rowKey === row.rowKey ? { ...x, ...updated } : x)));
      cancelEdit();
      loadHealth();
    } catch (err) {
      setEditError(err.message || 'Failed to save changes');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggleEnabled = async (row) => {
    setTogglingId(`${row.partitionKey}::${row.rowKey}`);
    try {
      const updated = await updateUserAppPairAllowlistEntry(row.partitionKey, row.rowKey, { enabled: !row.enabled });
      setRows((prev) => prev.map((x) => (x.partitionKey === row.partitionKey && x.rowKey === row.rowKey ? { ...x, ...updated } : x)));
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to toggle entry');
    } finally {
      setTogglingId(null);
    }
  };

  const handleRemove = async (row) => {
    setRemoving(true);
    try {
      await removeUserAppPairAllowlistEntry(row.partitionKey, row.rowKey);
      // Soft-delete: row is retained with enabled=false.
      setRows((prev) => prev.map((x) => (x.partitionKey === row.partitionKey && x.rowKey === row.rowKey ? { ...x, enabled: false } : x)));
      setConfirmRemove(null);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to disable entry');
    } finally {
      setRemoving(false);
    }
  };

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      await reconcileUserAppPairAllowlistSentinel(selectedRuleId);
      setConfirmReconcile(false);
      loadHealth();
    } catch (err) {
      setError(err.message || 'Failed to reconcile sentinel');
    } finally {
      setReconciling(false);
    }
  };

  const activeRuleName = allowlistRules.find((r) => r.ruleId === selectedRuleId)?.ruleName || selectedRuleId;

  const canSubmit = isAdmin && !adding && formTenantId &&
    (formUserMode === 'any' || (formUserMode === 'domain' ? formDomain.trim() : formUpn.trim())) &&
    (formAppMode === 'any' || formAppId.trim());

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <CubeTransparentIcon className="h-7 w-7 text-blue-500" />
          <h1 className="text-2xl font-bold text-gray-100">User / Application Pair Allowlist</h1>
        </div>
        <p className="text-sm text-gray-500 max-w-3xl">
          Benign <span className="text-gray-400">(user, application)</span> sign-in pairs for{' '}
          <span className="font-mono text-gray-400">{activeRuleName}</span> whose matches are suppressed. The pipeline
          matches on the <strong>application GUID</strong> — the display name is shown for convenience only. Use{' '}
          <span className="font-mono text-gray-400">*</span> for "any user" or "any app" (but not both), or{' '}
          <span className="font-mono text-gray-400">*@domain.tld</span> ("any guest from a domain") to suppress B2B
          guests from a partner domain — internal members are never affected by a domain entry. A known-malicious
          IP still alerts. Tenant + User + Application are the row's identity and immutable — only the note and display
          name are editable; delete soft-disables (the row is kept with{' '}
          <span className="font-mono text-gray-400">enabled=false</span>). The quickest way to add a pair is the
          <strong> Suppress</strong> action on a matching alert in Detection Alerts.
        </p>
      </div>

      {/* Rule tabs */}
      <div className="mb-5 border-b border-gray-800">
        <div className="flex flex-wrap items-center gap-1" role="tablist" aria-label="Detection rule">
          {allowlistRules.map((r) => {
            const active = r.ruleId === selectedRuleId;
            return (
              <button
                key={r.ruleId}
                role="tab"
                aria-selected={active}
                onClick={() => setSelectedRuleId(r.ruleId)}
                className={`px-4 py-2 text-sm font-medium rounded-t-md border-b-2 -mb-px transition-colors ${
                  active
                    ? 'border-blue-500 text-blue-300 bg-gray-800/40'
                    : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'
                }`}
              >
                {r.ruleName}
              </button>
            );
          })}
        </div>
      </div>

      {/* Sentinel health banner */}
      {health?.outboxPending && (
        <div className="mb-4 p-3 bg-amber-900/30 border border-amber-700/50 rounded-lg text-amber-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>
            Pending sync since {formatDate(health.outboxEnqueuedUtc)} ({health.outboxAttemptCount} attempt{health.outboxAttemptCount !== 1 ? 's' : ''}).
            The detection pipeline may not yet see recent changes. The retry timer runs every ~30&nbsp;s.
          </span>
          {isAdmin && (
            <button onClick={() => setConfirmReconcile(true)} className="ml-auto underline hover:no-underline text-amber-200">
              Reconcile now
            </button>
          )}
        </div>
      )}

      {confirmReconcile && (
        <div className="mb-4 p-3 bg-gray-900 border border-gray-700 rounded-lg text-sm flex items-center gap-3">
          <ArrowsRightLeftIcon className="h-4 w-4 text-blue-400 flex-shrink-0" />
          <span className="text-gray-300">Force-bump the sentinel? This recomputes the version monotonically and clears any pending outbox row.</span>
          <button onClick={handleReconcile} disabled={reconciling} className="btn-primary px-3 py-1 text-xs ml-auto">
            {reconciling ? 'Reconciling…' : 'Confirm'}
          </button>
          <button onClick={() => setConfirmReconcile(false)} className="btn-secondary px-3 py-1 text-xs">Cancel</button>
        </div>
      )}

      {/* Add form */}
      {isAdmin ? (
        <div className="card mb-6">
          <h2 className="text-sm font-semibold text-gray-300 mb-4">
            Add (User, Application) Pair
            <span className="text-gray-500 font-normal"> — {activeRuleName}</span>
          </h2>
          <form onSubmit={handleAdd} className="space-y-3">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Tenant</label>
                <select value={formTenantId} onChange={(e) => setFormTenantId(e.target.value)} className="input-field h-10 py-0" required>
                  <option value="">— Tenant —</option>
                  <option value="*">All tenants (*)</option>
                  {sortedTenants.map((t) => (
                    <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">User</label>
                <div className="flex items-center gap-3 mb-2 text-xs text-gray-400">
                  {[
                    { v: 'upn', label: 'Full UPN' },
                    { v: 'domain', label: 'Any guest from a domain' },
                    { v: 'any', label: 'Any user (*)' },
                  ].map((m) => (
                    <label key={m.v} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="userMode" value={m.v} checked={formUserMode === m.v}
                        onChange={() => { setFormUserMode(m.v); setFormUpn(''); setFormDomain(''); }}
                        className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                      {m.label}
                    </label>
                  ))}
                </div>
                {formUserMode === 'upn' && (
                  <input
                    type="text"
                    placeholder="e.g. user@contoso.com"
                    value={formUpn}
                    onChange={(e) => setFormUpn(e.target.value)}
                    className="input-field h-10 py-0 font-mono text-xs"
                  />
                )}
                {formUserMode === 'domain' && (
                  <>
                    <div className="flex items-center input-field h-10 py-0 font-mono text-xs">
                      <span className="text-gray-500 select-none">*@</span>
                      <input
                        type="text"
                        placeholder="westerncomputer.com"
                        value={formDomain}
                        onChange={(e) => setFormDomain(e.target.value)}
                        className="bg-transparent border-0 focus:ring-0 flex-1 p-0 ml-0.5 text-gray-200"
                      />
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">
                      Suppresses any guest/B2B user whose sign-in UPN ends with this domain. Only external
                      (B2B) accounts are affected — internal members with a matching domain are not.
                    </p>
                  </>
                )}
                {formUserMode === 'any' && (
                  <p className="text-xs text-amber-300 h-10 flex items-center">Any user in this tenant will match (pair with a specific app).</p>
                )}
              </div>
            </div>

            {/* Application picker */}
            <div className="flex flex-col lg:flex-row gap-3 lg:items-start">
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Application</label>
                <div className="flex items-center gap-3 mb-2 text-xs text-gray-400">
                  {[
                    { v: 'pick', label: 'Pick known app' },
                    { v: 'manual', label: 'Enter GUID' },
                    { v: 'any', label: 'Any app (*)' },
                  ].map((m) => (
                    <label key={m.v} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="appMode" value={m.v} checked={formAppMode === m.v}
                        onChange={() => { setFormAppMode(m.v); setFormAppId(''); setFormAppName(''); }}
                        className="border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500" />
                      {m.label}
                    </label>
                  ))}
                </div>
                {formAppMode === 'pick' && (
                  <select value={formAppId} onChange={(e) => handleAppPick(e.target.value)} className="input-field h-10 py-0">
                    <option value="">— Select known app —</option>
                    {sortedApps.map((a) => (
                      <option key={`${a.tenantId}:${a.appId}`} value={a.appId}>
                        {a.displayName || a.appId}{a.tenantId === 'GLOBAL' ? ' (global)' : ''}
                      </option>
                    ))}
                  </select>
                )}
                {formAppMode === 'manual' && (
                  <div className="flex flex-col sm:flex-row gap-2">
                    <input type="text" placeholder="App GUID (38aa3b87-…)" value={formAppId}
                      onChange={(e) => setFormAppId(e.target.value)} className="input-field h-10 py-0 font-mono text-xs flex-1" />
                    <input type="text" placeholder="Display name (optional)" value={formAppName}
                      onChange={(e) => setFormAppName(e.target.value)} className="input-field h-10 py-0 flex-1" />
                  </div>
                )}
                {formAppMode === 'any' && (
                  <p className="text-xs text-amber-300 h-10 flex items-center">Any application in this tenant will match (use sparingly).</p>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <label className="block text-xs text-gray-500 mb-1">Note</label>
                <input
                  type="text"
                  placeholder="e.g. Windows Sign In is benign for this rule"
                  value={formNote}
                  onChange={(e) => setFormNote(e.target.value)}
                  className="input-field h-10 py-0"
                />
              </div>
              <div className="flex-shrink-0 lg:pt-[26px]">
                <button type="submit" disabled={!canSubmit} className="btn-primary flex items-center gap-2 whitespace-nowrap">
                  {adding ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlusIcon className="h-4 w-4" />}
                  Add Pair
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-600">
              Matching is on the <strong>application GUID</strong>. Re-adding an existing pair is an idempotent upsert
              that also re-enables a soft-disabled row. The <span className="font-mono">(any user, any app)</span> pair is
              rejected.
            </p>
          </form>
          {addError && <p className="mt-2 text-sm text-red-400">{addError}</p>}
        </div>
      ) : (
        <div className="card mb-6 flex items-center gap-3 text-sm text-gray-400">
          <LockClosedIcon className="h-4 w-4 text-gray-500" />
          <span>Read-only view — admin access is required to add, edit, or disable entries.</span>
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => loadEntries(selectedRuleId)} className="ml-auto underline hover:no-underline text-red-400">Retry</button>
        </div>
      )}

      {editError && (
        <div className="mb-4 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-red-300 text-sm flex items-center gap-2">
          <ExclamationTriangleIcon className="h-4 w-4 flex-shrink-0" />
          <span>{editError}</span>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
            <input
              type="text"
              placeholder="Filter by user, app, note, added by..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              className="input-field pl-9"
            />
          </div>
          <select value={tenantFilter} onChange={(e) => setTenantFilter(e.target.value)} className="input-field sm:w-64">
            <option value="all">All tenants</option>
            <option value="*">Cross-tenant (*) only</option>
            {sortedTenants.map((t) => (
              <option key={t.TenantID} value={t.TenantID}>{t.FriendlyName || t.TenantID}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-xs text-gray-400 cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              className="rounded border-gray-600 bg-gray-800 text-blue-500 focus:ring-blue-500"
            />
            Show disabled
          </label>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-500">
          {isAdmin && (
            <button
              onClick={() => setConfirmReconcile(true)}
              disabled={reconciling}
              className="btn-secondary flex items-center gap-1.5 text-xs"
              title="Force-bump the sentinel (recovery)"
            >
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
              Reconcile sentinel
            </button>
          )}
          <span>{filtered.length} pair{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      {/* Table */}
      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-sm" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
            <thead>
              <tr className="border-b border-gray-800">
                {[
                  { label: 'Tenant',      sortKey: 'tenantId' },
                  { label: 'User',        sortKey: 'userPrincipalName' },
                  { label: 'Application', sortKey: 'applicationDisplayName' },
                  { label: 'Enabled',     sortKey: 'enabled' },
                  { label: 'Note',        sortKey: null },
                  { label: 'Added By',    sortKey: 'addedBy' },
                  { label: 'Date',        sortKey: 'addedUtc' },
                  { label: 'Actions',     sortKey: null },
                ].map((col, colIdx) => (
                  <th
                    key={col.label}
                    ref={(el) => { thRefs.current[colIdx] = el; }}
                    onClick={col.sortKey ? () => toggleSort(col.sortKey) : undefined}
                    className={`${colIdx === 7 ? 'text-center' : 'text-left'} text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 whitespace-nowrap relative select-none ${col.sortKey ? 'cursor-pointer hover:text-gray-300' : ''}`}
                    style={{ width: `${colWidths[colIdx]}px` }}
                  >
                    <span className="truncate">{col.label}</span>
                    {col.sortKey && <SortIndicator k={col.sortKey} />}
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => { e.stopPropagation(); startResize(e, colIdx); }}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800/50">
              {loading && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      <span className="text-gray-500 text-sm">Loading allowlist...</span>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center">
                    <CubeTransparentIcon className="h-10 w-10 text-gray-700 mx-auto mb-3" />
                    <p className="text-gray-400 font-medium">No allowlisted pairs</p>
                    <p className="text-gray-600 text-sm mt-1">
                      {filterQuery || tenantFilter !== 'all' || !showDisabled
                        ? 'Try adjusting your filters.'
                        : isAdmin ? 'Add a pair above, or use Suppress on a matching alert.' : 'No entries yet.'}
                    </p>
                  </td>
                </tr>
              )}

              {!loading && paged.map((r) => {
                const compositeKey = `${r.partitionKey}::${r.rowKey}`;
                const isEditing = editingId === compositeKey;
                const isEnabled = r.enabled !== false;
                return (
                <tr key={compositeKey} className={`hover:bg-gray-800/50 transition-colors ${isEnabled ? '' : 'opacity-60'}`}>
                  <td className="px-4 py-3 text-gray-200 truncate overflow-hidden" title={r.tenantId}>
                    {r.tenantId === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">All tenants (*)</span>
                    ) : (
                      tenantNameById[r.tenantId] || r.tenantId
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-300 truncate overflow-hidden" title={r.userPrincipalName}>
                    {r.userPrincipalName === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs font-sans">Any user (*)</span>
                    ) : domainWildcardOf(r.userPrincipalName) ? (
                      <span className="inline-flex items-center gap-1.5 font-sans">
                        <Badge tone="purple">Domain</Badge>
                        <span className="text-gray-300 truncate">Any guest from {domainWildcardOf(r.userPrincipalName)}</span>
                      </span>
                    ) : (
                      r.userPrincipalName
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-300 truncate overflow-hidden" title={`${r.applicationDisplayName || ''} ${r.applicationId || ''}`}>
                    {r.applicationId === '*' ? (
                      <span className="px-1.5 py-0.5 rounded bg-amber-900/30 text-amber-300 border border-amber-700/40 text-xs">Any app (*)</span>
                    ) : isEditing ? (
                      <input type="text" value={editAppName} onChange={(e) => setEditAppName(e.target.value)}
                        placeholder="Display name" className="input-field w-full text-xs py-1" />
                    ) : (
                      <div className="min-w-0">
                        <div className="truncate">{r.applicationDisplayName || <span className="text-gray-600 italic">(no name)</span>}</div>
                        <div className="font-mono text-[10px] text-gray-600 truncate">{r.applicationId}</div>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {isAdmin ? (
                      <button
                        onClick={() => handleToggleEnabled(r)}
                        disabled={togglingId === compositeKey}
                        title={isEnabled ? 'Click to disable' : 'Click to enable'}
                        className="focus:outline-none"
                      >
                        <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                      </button>
                    ) : (
                      <Badge tone={isEnabled ? 'success' : 'neutral'}>{isEnabled ? 'Enabled' : 'Disabled'}</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 truncate overflow-hidden" title={r.note || ''}>
                    {isEditing ? (
                      <input type="text" value={editNote} onChange={(e) => setEditNote(e.target.value)} className="input-field w-full text-xs py-1" autoFocus />
                    ) : (
                      r.note || '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs truncate overflow-hidden" title={r.addedBy || ''}>{r.addedBy || '—'}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs truncate overflow-hidden">{formatDate(r.addedUtc)}</td>
                  <td className="px-4 py-3 text-center">
                    {!isAdmin ? (
                      <span className="text-gray-700">{'—'}</span>
                    ) : isEditing ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleSaveEdit(r)} disabled={savingEdit} className="text-gray-600 hover:text-green-400 transition-colors p-1" title="Save">
                          {savingEdit ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
                        </button>
                        <button onClick={cancelEdit} disabled={savingEdit} className="text-gray-600 hover:text-gray-300 transition-colors p-1" title="Cancel">
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ) : confirmRemove === compositeKey ? (
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleRemove(r)} disabled={removing} className="btn-danger px-2 py-1 text-xs">
                          {removing ? 'Disabling...' : 'Confirm'}
                        </button>
                        <button onClick={() => setConfirmRemove(null)} className="btn-secondary px-2 py-1 text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(r)} className="text-gray-600 hover:text-blue-400 transition-colors p-1" title="Edit note / display name (Tenant / User / App are immutable — delete and re-add to change them)">
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button onClick={() => setConfirmRemove(compositeKey)} disabled={!isEnabled} className="text-gray-600 hover:text-red-400 transition-colors p-1 disabled:opacity-30 disabled:cursor-not-allowed" title={isEnabled ? 'Disable (soft-delete) — the rule will resume alerting on this pair' : 'Already disabled'}>
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!loading && filtered.length > 0 && (
          <Pagination
            currentPage={safeCurrentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}
